import type { Transaction } from "@rocicorp/zero";
import {
  type AIProviderId,
  getAIProvider,
  managedAIProviderIdForModel,
  resolveAIProviderId,
} from "../aiProviders";
import {
  attachmentSizeLimit,
  isSupportedAttachmentMediaType,
} from "../attachmentPolicy";
import { userFacingError } from "../authorization";
import { type Schema, zql } from "../generatedDrizzleSchema";

type AttachmentInput = {
  fileName: string;
  mediaType: string;
  sizeBytes: number;
  data: string;
};

async function resolveCurrentAIProviderId(
  tx: {
    run: Pick<Transaction<Schema>, "run">["run"];
  },
  chatModel?: string
): Promise<AIProviderId | null> {
  if (managedAIProviderIdForModel("anthropic", chatModel) === "openai") {
    return "openai";
  }
  const [config] = (await tx.run(
    zql.aiProviders.where("active", true).limit(1)
  )) as Array<{ provider?: string | null; baseUrl?: string | null }>;
  return config ? resolveAIProviderId(config) : null;
}

export async function assertAttachmentsSupported(
  tx: {
    run: Pick<Transaction<Schema>, "run">["run"];
  },
  attachments?: AttachmentInput[],
  chatModel?: string
) {
  if (!attachments?.length) return;

  const providerId = await resolveCurrentAIProviderId(tx, chatModel);
  if (!providerId) return;
  const providerLabel = getAIProvider(providerId).label;

  for (const attachment of attachments) {
    if (!isSupportedAttachmentMediaType(providerId, attachment.mediaType)) {
      throw userFacingError(
        `${providerLabel} does not support ${attachment.fileName} (${attachment.mediaType})`
      );
    }

    const sizeBytes = atob(attachment.data).length;
    const limit = attachmentSizeLimit(attachment.mediaType);
    if (sizeBytes > limit) {
      throw userFacingError(
        `${attachment.fileName} exceeds the ${providerLabel} attachment limit for ${attachment.mediaType}`
      );
    }
  }
}
